import { FC, useEffect } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import {
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import { GiftOrderPaymentStatus } from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import {
  desiredDeliveryPaymentNotice,
  formatDeliveryDate,
} from '../utils/giftDeliveryDate.util';

const GiftOrderPaymentResultPage: FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const result = searchParams.get('success');
  const bugoId = searchParams.get('bugoId');

  const isSuccess = result === 'true';

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();

  // 주문 데이터 조회 (StoreCustomer/StoreGuest 역할 사용)
  useGiftOrderForStoreHook(orderId ?? '', storeRole(), !orderId);
  const status = useRecoilValue(giftOrderForStoreStatusAtomFamily(orderId ?? ''));
  const orderData = useRecoilValue(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  useEffect(() => {
    setTitle(isSuccess ? '주문 완료' : '주문 실패');
  }, [setTitle, isSuccess]);

  // 로딩 중
  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // 첫 번째 상품 정보 (대표)
  const firstItem = orderData?.orderDetails?.[0];
  const totalItems = orderData?.orderDetails?.length ?? 0;
  const totalQuantity =
    orderData?.orderDetails?.reduce(
      (sum: number, d: { quantity: number }) => sum + d.quantity,
      0,
    ) ?? 0;

  const desiredDateLabel = formatDeliveryDate(orderData?.desiredDeliveryDate);
  const isPaid =
    orderData?.paymentDetail?.status === GiftOrderPaymentStatus.Paid ||
    orderData?.paymentDetail?.status === GiftOrderPaymentStatus.Confirmed;
  const desiredDateNotice = desiredDateLabel
    ? desiredDeliveryPaymentNotice({
        date: orderData?.desiredDeliveryDate,
        requestedDate: orderData?.desiredDeliveryDateRequested,
        payBy: orderData?.desiredDeliveryPayBy,
        awaitingDeposit: !isPaid && !!orderData?.paymentDetail?.vBank,
      })
    : '';

  // 상품명 생성
  const productName =
    totalItems > 1
      ? `${firstItem?.giftItemEmbed?.name || '상품'} 외 ${totalItems - 1}건`
      : firstItem?.giftItemEmbed?.name || '상품';

  // 이미지 URL
  const imageUrl =
    typeof firstItem?.giftItem === 'object' &&
    firstItem?.giftItem?.giftItemInfo?.image?.url
      ? firstItem.giftItem.giftItemInfo.image.url
      : typeof firstItem?.giftItem === 'object' && firstItem?.giftItem?.image?.url
      ? firstItem.giftItem.image.url
      : '';

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 결과 아이콘 & 메시지 */}
      <div className="flex flex-col items-center bg-white py-10">
        {isSuccess ? (
          <CheckCircleIcon className="h-20 w-20 text-green-500" />
        ) : (
          <XCircleIcon className="h-20 w-20 text-red-500" />
        )}
        <h2 className="mt-4 text-xl font-bold">
          주문이 {isSuccess ? '완료' : '실패'}되었습니다
        </h2>
        {orderData && (
          <p className="mt-2 text-sm text-gray-500">
            {formatDate(orderData.createdAt, { dateSeparater: '.' })} 주문
          </p>
        )}
        {orderData && (
          <p className="mt-1 text-xs text-gray-400">주문번호: {orderData._id}</p>
        )}
      </div>

      {/* 가상계좌 정보 (있는 경우) */}
      {orderData?.paymentDetail?.vBank && (
        <div className="mt-2 bg-white p-4">
          <h3 className="mb-2 font-bold text-blue-600">가상계좌 입금 안내</h3>
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="whitespace-pre-line text-sm">{orderData.paymentDetail.vBank}</p>
            <p className="mt-2 text-xs text-gray-500">입금 확인 후 주문이 완료됩니다.</p>
          </div>
        </div>
      )}

      {/* 상품 정보 */}
      {orderData && (
        <div className="mt-2 bg-white p-4">
          <h3 className="mb-3 font-bold">주문 상품</h3>
          <div className="flex gap-3">
            {/* 이미지 */}
            <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded bg-gray-100">
              {imageUrl ? (
                <img
                  src={fileBucketUrl(imageUrl, 'w200')}
                  alt={productName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                  No Image
                </div>
              )}
            </div>
            {/* 상품 정보 */}
            <div className="flex-1">
              <p className="font-medium">{productName}</p>
              <p className="mt-1 text-sm text-gray-500">수량: {totalQuantity}개</p>
            </div>
          </div>
        </div>
      )}

      {/* 받으실 날짜 (날짜 지정 브랜드 주문만) */}
      {desiredDateLabel && (
        <div className="mt-2 bg-white p-4">
          <h3 className="mb-2 font-bold">받으실 날짜</h3>
          <p className="text-sm font-bold theme-text-main">{desiredDateLabel}</p>
          {/* 기준은 결제(입금) 완료 시각 — 입금 대기 중이면 기한, 기한을 넘겨 결제돼 날짜가 바뀌었으면 변경 안내 */}
          {desiredDateNotice && (
            <p className="mt-1.5 break-keep text-xs font-bold text-red-500">
              {desiredDateNotice}
            </p>
          )}
          <p className="mt-1 break-keep text-xs text-gray-400">
            날짜 변경은 고객센터로 전화 주세요.
          </p>
        </div>
      )}

      {/* 결제 금액 */}
      {orderData && (
        <div className="mt-2 bg-white p-4">
          <h3 className="mb-3 font-bold">결제 금액</h3>
          {/* 권역 배송비 스냅샷이 있으면 상품/배송비로 나눠 보여준다 (구서버 주문엔 없음) */}
          {orderData.deliveryFee && (
            <div className="mb-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">상품 금액</span>
                <span>
                  {formatPrice(
                    orderData.paymentDetail.paymentByCash - orderData.deliveryFee.total,
                  )}
                  원
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  배송비
                  {orderData.deliveryFee.extraFee > 0 && (
                    <span className="ml-1 text-[11px] text-orange-600">
                      지역 추가배송비 포함
                    </span>
                  )}
                </span>
                <span>
                  {orderData.deliveryFee.total === 0
                    ? '무료'
                    : `${formatPrice(orderData.deliveryFee.total)}원`}
                </span>
              </div>
            </div>
          )}
          <div
            className={`flex justify-between text-lg ${
              orderData.deliveryFee ? 'border-t pt-2' : ''
            }`}
          >
            <span>총 결제 금액</span>
            <span className="font-bold text-red-500">
              {formatPrice(orderData.paymentDetail.paymentByCash)}원
            </span>
          </div>
        </div>
      )}

      {/* 버튼 영역 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-4 shadow-lg">
        <div className="mx-auto flex max-w-lg gap-3">
          <Link
            to={`/gift-store?bugoId=${bugoId}`}
            className="flex-1 rounded-lg border border-gray-300 py-3 text-center font-bold text-gray-700"
          >
            쇼핑 계속하기
          </Link>
          <Link
            to={`/gift-store/orders?bugoId=${bugoId}`}
            className="flex-1 rounded-lg bg-red-500 py-3 text-center font-bold text-white"
          >
            구매내역 보기
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GiftOrderPaymentResultPage;
